import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './loginbinder.css';
import { CLOUD_API_BASE_URL, createApiClient } from '../lib/apiClient';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useUserId } from '../UserIdContext';

const SAVED_LOGIN_KEY = 'binder_saved_login';
const DEVICE_ID_KEY = 'binder_device_id';

const createDeviceId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const randomPart = Array.from(window.crypto?.getRandomValues?.(new Uint32Array(4)) || [])
    .map((value) => value.toString(16).padStart(8, '0'))
    .join('');
  return randomPart || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateDeviceId = () => {
  const current = String(localStorage.getItem(DEVICE_ID_KEY) || '').trim();
  if (current.length >= 24) return current;
  const next = createDeviceId();
  localStorage.setItem(DEVICE_ID_KEY, next);
  return next;
};

const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || CLOUD_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

function Loginbinder() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUser, setRememberUser] = useState(false);
  const [deviceId] = useState(() => getOrCreateDeviceId());
  const [whatsappChallenge, setWhatsappChallenge] = useState(null);
  const [whatsappCode, setWhatsappCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [securityData, setSecurityData] = useState({
    ip: 'Cargando...',
    ubicacion: 'Cargando...',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useUserId();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const savedCredentials = localStorage.getItem(SAVED_LOGIN_KEY);
    if (!savedCredentials) return;

    try {
      const { email: savedEmail } = JSON.parse(savedCredentials);
      if (savedEmail) setEmail(savedEmail);
      setRememberUser(true);
      localStorage.setItem(SAVED_LOGIN_KEY, JSON.stringify({ email: savedEmail || '' }));
    } catch (parseError) {
      localStorage.removeItem(SAVED_LOGIN_KEY);
    }
  }, []);

  useEffect(() => {
    if (!whatsappChallenge || secondsLeft <= 0) return undefined;
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [secondsLeft, whatsappChallenge]);

  useEffect(() => {
    let ignore = false;

    const loadSecurityData = async () => {
      try {
        const { data } = await api.get('/api/geo/acceso');
        if (ignore) return;

        setSecurityData({
          ip: data?.ip || 'No disponible',
          ubicacion: data?.ubicacion || 'No disponible',
        });
      } catch (err) {
        if (ignore) return;

        setSecurityData({
          ip: 'No disponible',
          ubicacion: 'No disponible',
        });
      }
    };

    loadSecurityData();

    return () => {
      ignore = true;
    };
  }, []);

  const redirigirAGeneral = () => {
    window.location.href = '/iniciogeneral';
  };

  const syncSavedCredentials = () => {
    if (!rememberUser) {
      localStorage.removeItem(SAVED_LOGIN_KEY);
      return;
    }

    localStorage.setItem(
      SAVED_LOGIN_KEY,
      JSON.stringify({ email })
    );
  };

  const handleRememberUserChange = (event) => {
    const shouldRemember = event.target.checked;
    setRememberUser(shouldRemember);

    if (!shouldRemember) {
      localStorage.removeItem(SAVED_LOGIN_KEY);
    }
  };

  const persistSession = (userData, tokenData) => {
    setSuccess('Inicio de sesion exitoso');
    if (tokenData) {
      localStorage.setItem('token', tokenData);
    }
    if (userData) {
      localStorage.setItem('usuario', JSON.stringify(userData));
      setUser(userData);
    }
  };

  const requestLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data } = await api.post('/api/login', { email, password, deviceId });
      const {
        ok,
        token,
        user,
        msg,
        requiresWhatsappToken,
        challengeId,
        maskedPhone,
        expiresIn,
      } = data || {};

      if (ok === false) {
        setError(msg || 'Credenciales incorrectas.');
        return;
      }

      if (requiresWhatsappToken && challengeId) {
        setWhatsappChallenge({ challengeId, maskedPhone: maskedPhone || '' });
        setWhatsappCode('');
        setSecondsLeft(Number(expiresIn) || 300);
        setSuccess(msg || 'Enviamos un codigo de 6 digitos por WhatsApp.');
        syncSavedCredentials();
        return;
      }

      if (!token || !user) {
        throw new Error('Respuesta invalida del servidor');
      }

      persistSession(user, token);
      syncSavedCredentials();
      setTimeout(redirigirAGeneral, 600);
    } catch (err) {
      setError(
        err?.response?.data?.msg ||
          err?.message ||
          'Credenciales incorrectas o error del servidor.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (event) => {
    event.preventDefault();
    requestLogin();
  };

  const handleWhatsappVerification = async (event) => {
    event.preventDefault();
    if (!whatsappChallenge?.challengeId || !/^\d{6}$/.test(whatsappCode)) {
      setError('Ingresa el codigo de 6 digitos recibido por WhatsApp.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/login/verify-whatsapp', {
        challengeId: whatsappChallenge.challengeId,
        code: whatsappCode,
        deviceId,
      });
      if (!data?.ok || !data?.token || !data?.user) {
        throw new Error(data?.msg || 'No se pudo validar el codigo.');
      }
      persistSession(data.user, data.token);
      syncSavedCredentials();
      setTimeout(redirigirAGeneral, 600);
    } catch (err) {
      setError(err?.response?.data?.msg || err?.message || 'No se pudo validar el codigo.');
    } finally {
      setLoading(false);
    }
  };

  const resetWhatsappChallenge = () => {
    setWhatsappChallenge(null);
    setWhatsappCode('');
    setSecondsLeft(0);
    setError('');
    setSuccess('');
  };

  return (
    <div className="login-container-binderx">
      <div className="login-shell-binderx">
        <div className="login-card-binderx p-4 shadow-lg rounded" data-aos="zoom-in">
          <div className="text-center mb-4 binder-logo-wrap" data-aos="fade-down">
            <img src="/logobinder.png" alt="Binder Logo" className="binder-logo-img" />
          </div>

          <form
            onSubmit={whatsappChallenge ? handleWhatsappVerification : handleLogin}
            autoComplete="on"
          >
            {!whatsappChallenge ? (
              <>
                <div className="form-group mb-3" data-aos="fade-up" data-aos-delay="100">
                  <label className="text-light">Usuario</label>
                  <input
                    type="text"
                    name="username"
                    className="form-control bg-dark text-light border-success"
                    placeholder="Ingrese su usuario"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>

                <div className="form-group mb-4" data-aos="fade-up" data-aos-delay="200">
                  <label className="text-light">Contrasena</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className="form-control bg-dark text-light border-success"
                      placeholder="Ingrese su contrasena"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword((currentValue) => !currentValue)}
                      aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-check mb-4" data-aos="fade-up" data-aos-delay="260">
                  <input
                    id="rememberUser"
                    className="form-check-input"
                    type="checkbox"
                    checked={rememberUser}
                    onChange={handleRememberUserChange}
                  />
                  <label className="form-check-label text-light" htmlFor="rememberUser">
                    Recordar usuario
                  </label>
                </div>
              </>
            ) : (
              <div className="whatsapp-token-step" data-aos="fade-up">
                <div className="whatsapp-token-icon" aria-hidden="true">WA</div>
                <h2>Verifica este dispositivo</h2>
                <p>
                  Ingresa el codigo de 6 digitos enviado por WhatsApp a{' '}
                  <strong>{whatsappChallenge.maskedPhone || 'tu celular registrado'}</strong>.
                </p>
                <input
                  type="text"
                  name="whatsappCode"
                  className="form-control whatsapp-token-input"
                  value={whatsappCode}
                  onChange={(event) =>
                    setWhatsappCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                />
                <div className={`whatsapp-token-expiry ${secondsLeft <= 0 ? 'expired' : ''}`}>
                  {secondsLeft > 0
                    ? `El codigo vence en ${Math.floor(secondsLeft / 60)}:${String(
                        secondsLeft % 60
                      ).padStart(2, '0')}`
                    : 'El codigo vencio. Solicita uno nuevo.'}
                </div>
                <div className="whatsapp-token-secondary-actions">
                  <button type="button" onClick={resetWhatsappChallenge} disabled={loading}>
                    Volver
                  </button>
                  <button type="button" onClick={requestLogin} disabled={loading}>
                    Enviar otro codigo
                  </button>
                </div>
              </div>
            )}

            <div data-aos="fade-up" data-aos-delay="300">
              <button
                className="btn btn-success w-100 fw-bold"
                type="submit"
                disabled={loading || Boolean(whatsappChallenge && secondsLeft <= 0)}
              >
                {loading
                  ? whatsappChallenge
                    ? 'Validando...'
                    : 'Ingresando...'
                  : whatsappChallenge
                    ? 'Validar codigo'
                    : 'Iniciar Sesion'}
              </button>
            </div>

            <div data-aos="fade-up" data-aos-delay="400">
              {error && <div className="text-danger mt-3">{error}</div>}
              {success && <div className="text-success mt-3">{success}</div>}
            </div>
          </form>
        </div>

        <div className="login-security-note" data-aos="fade-up" data-aos-delay="460">
          <p className="login-security-note-title">Por politica de seguridad</p>
          <p>
            Su IP es <span>{securityData.ip}</span>
          </p>
          <p>
            Su ubicacion es <span>{securityData.ubicacion}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Loginbinder;
